import { ComponentType } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { Box, Typography, Button, Avatar, IconButton } from '@mui/material';
import {
  HomeOutlined,
  Search,
  SettingsOutlined,
  PersonOutline,
  Add,
  PlaceOutlined,
} from '@mui/icons-material';
import { SaveBookmarkOutlineIcon } from '../icons/SaveBookmarkIcon';
import DesignSystem from '../../designSystem';
import { isFeatureEnabled } from '../../config/featureFlags';
import { useAuth } from '../../contexts/AuthContext';
import { useT } from '../../i18n/useT';
import { StringKey } from '../../i18n/strings';
import LocalePreferenceToggles from './LocalePreferenceToggles';

export const DESKTOP_SIDEBAR_WIDTH = 288;
export const MOBILE_NAV_BAR_HEIGHT = 58;
export const MOBILE_NAV_BOTTOM_GAP = 6;
export const MOBILE_BOTTOM_CHROME = MOBILE_NAV_BOTTOM_GAP + MOBILE_NAV_BAR_HEIGHT + 12;

type NavItem = {
  labelKey: StringKey;
  icon: ComponentType<SvgIconProps>;
  path: string;
  isCreate?: boolean;
};

const desktopNavItems: NavItem[] = [
  { labelKey: 'navHome', icon: HomeOutlined, path: '/homepage' },
  { labelKey: 'navMap', icon: PlaceOutlined, path: '/map' },
  { labelKey: 'navSearch', icon: Search, path: '/search' },
  { labelKey: 'navLists', icon: SaveBookmarkOutlineIcon, path: '/bookmarks' },
  { labelKey: 'navProfile', icon: PersonOutline, path: '/profile' },
  { labelKey: 'navSettings', icon: SettingsOutlined, path: '/settings' },
];

const mobileNavItems: NavItem[] = [
  { labelKey: 'navHome', icon: HomeOutlined, path: '/homepage' },
  { labelKey: 'navLists', icon: SaveBookmarkOutlineIcon, path: '/bookmarks' },
  { labelKey: 'navCreate', icon: Add, path: '/visi/create', isCreate: true },
  { labelKey: 'navProfile', icon: PersonOutline, path: '/profile' },
  { labelKey: 'navSearch', icon: Search, path: '/search' },
];

const API_BASE = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : '');

function desktopPathIndex(pathname: string): number {
  if (pathname === '/homepage') return 0;
  if (pathname === '/map') return 1;
  if (pathname.includes('/search')) return 2;
  if (pathname.includes('/bookmarks')) return 3;
  if (pathname.includes('/profile') || pathname.startsWith('/visi/edit') || pathname.startsWith('/visi/qr')) return 4;
  if (pathname.includes('/settings')) return 5;
  return -1;
}

function mobilePathIndex(pathname: string): number {
  if (pathname === '/homepage') return 0;
  if (pathname.includes('/bookmarks')) return 1;
  if (pathname.includes('/visi/create')) return 2;
  if (pathname.includes('/profile') || pathname.startsWith('/visi/edit') || pathname.startsWith('/visi/qr')) return 3;
  if (pathname.includes('/search')) return 4;
  return -1;
}

function MobileTabButton({
  item,
  selected,
  label,
  onClick,
}: {
  item: NavItem;
  selected: boolean;
  label: string;
  onClick: () => void;
}) {
  const Icon = item.icon;

  if (item.isCreate) {
    return (
      <IconButton
        onClick={onClick}
        aria-label={label}
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          bgcolor: DesignSystem.colors.action,
          color: DesignSystem.colors.onPrimary,
          flexShrink: 0,
          boxShadow: `0 6px 18px ${DesignSystem.colors.actionLight}`,
          '&:hover': {
            bgcolor: DesignSystem.colors.actionDark,
          },
        }}
      >
        <Add sx={{ fontSize: 26, fontWeight: 700 }} />
      </IconButton>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-current={selected ? 'page' : undefined}
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        height: MOBILE_NAV_BAR_HEIGHT,
        border: 0,
        bgcolor: 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
      }}
    >
      {selected && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: DesignSystem.colors.primaryLight,
            boxShadow: `0 0 18px ${DesignSystem.colors.primaryLight}`,
          }}
        />
      )}
      <Icon
        sx={{
          position: 'relative',
          zIndex: 1,
          fontSize: 22,
          color: selected ? DesignSystem.colors.primary : DesignSystem.colors.textPrimary,
          opacity: selected ? 1 : 0.82,
        }}
      />
    </Box>
  );
}

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const t = useT();
  const desktopRailEnabled = isFeatureEnabled('webDesktopNavigationRail');
  const desktopValue = desktopPathIndex(location.pathname);
  const mobileValue = mobilePathIndex(location.pathname);

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.email?.split('@')[0] ||
    t('navAccount');
  const avatarSrc =
    user?.profilePicture &&
    (user.profilePicture.startsWith('http')
      ? user.profilePicture
      : `${API_BASE}/assets/img/site/clients/img/${encodeURIComponent(user.profilePicture)}`);
  const initials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.trim() ||
    (user?.email?.[0]?.toUpperCase() ?? '?');

  return (
    <>
      <Box
        component="nav"
        aria-label={t('primaryNavAria')}
        sx={{
          position: 'fixed',
          bottom: `calc(env(safe-area-inset-bottom, 0px) + ${MOBILE_NAV_BOTTOM_GAP}px)`,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(374px, calc(100% - 20px))',
          height: MOBILE_NAV_BAR_HEIGHT,
          zIndex: 100,
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          borderRadius: DesignSystem.borderRadius.pill,
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          bgcolor: 'rgba(26, 26, 26, 0.78)',
          border: `0.5px solid ${DesignSystem.colors.separator}`,
          boxShadow: '0 8px 22px rgba(0, 0, 0, 0.28)',
        }}
      >
        {mobileNavItems.map((item, idx) => (
          <MobileTabButton
            key={item.path}
            item={item}
            selected={mobileValue === idx && !item.isCreate}
            label={t(item.labelKey)}
            onClick={() => navigate(item.path)}
          />
        ))}
      </Box>

      {desktopRailEnabled && (
        <Box
          component="aside"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: DESKTOP_SIDEBAR_WIDTH,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            zIndex: 1200,
            backgroundColor: DesignSystem.colors.canvas,
            borderRight: 'none',
            px: 2.5,
            py: 3,
            overflowY: 'auto',
          }}
        >
          <Box
            onClick={() => navigate('/homepage')}
            sx={{ px: 1, mb: 3.5, cursor: 'pointer', userSelect: 'none' }}
          >
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 800,
                letterSpacing: '-0.04em',
                color: DesignSystem.colors.primary,
                lineHeight: 1.1,
              }}
            >
              VisiMe
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
            {desktopNavItems.map((item, idx) => {
              const selected = desktopValue === idx;
              const Icon = item.icon;
              return (
                <Box
                  key={item.path}
                  component="button"
                  type="button"
                  onClick={() => navigate(item.path)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.75,
                    border: 0,
                    borderRadius: DesignSystem.borderRadius.pill,
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit',
                    color: selected ? DesignSystem.colors.textPrimary : DesignSystem.colors.textSecondary,
                    backgroundColor: selected ? DesignSystem.desktop.navActiveBg : 'transparent',
                    fontWeight: selected ? DesignSystem.desktop.navActiveWeight : 500,
                    transition: `background-color ${DesignSystem.motion.duration} ${DesignSystem.motion.easing}, color ${DesignSystem.motion.duration} ${DesignSystem.motion.easing}`,
                    '&:hover': {
                      backgroundColor: selected ? DesignSystem.desktop.navActiveBg : DesignSystem.colors.hover,
                      color: DesignSystem.colors.textPrimary,
                    },
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 27,
                      color: selected ? DesignSystem.colors.textPrimary : 'inherit',
                    }}
                  />
                  <Typography sx={{ fontSize: 17, fontWeight: 'inherit', color: 'inherit', letterSpacing: '-0.015em' }}>
                    {t(item.labelKey)}
                  </Typography>
                </Box>
              );
            })}

          </Box>

          <Box sx={{ mt: 3, width: '100%' }}>
            <Box
              sx={{
                p: 2,
                borderRadius: DesignSystem.borderRadius.lg,
                backgroundColor: DesignSystem.colors.surface,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.25 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: DesignSystem.colors.primaryLight,
                  }}
                >
                  <PlaceOutlined sx={{ fontSize: 19, color: DesignSystem.colors.primary }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 750,
                    lineHeight: 1.25,
                    letterSpacing: '-0.02em',
                    color: DesignSystem.colors.textPrimary,
                  }}
                >
                  {t('promoTitle')}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 13.5,
                  lineHeight: 1.45,
                  color: DesignSystem.colors.textSecondary,
                  mb: 1.5,
                }}
              >
                {t('promoBody')}
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/visi-info')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 14,
                  py: 1,
                  boxShadow: 'none',
                  borderRadius: DesignSystem.borderRadius.pill,
                  backgroundColor: DesignSystem.colors.action,
                  color: DesignSystem.colors.onPrimary,
                  '&:hover': { backgroundColor: DesignSystem.colors.actionDark, boxShadow: 'none' },
                }}
              >
                {t('promoCta')}
              </Button>
            </Box>

            <Box
              sx={{
                mt: 1.25,
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                columnGap: 1,
                alignItems: 'start',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <LocalePreferenceToggles dense showLabels appearanceShowLabels={false} />
              </Box>
            </Box>
          </Box>

          <Box
            onClick={() => navigate('/profile')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.15,
              mt: 1.5,
              px: 1,
              py: 0.85,
              borderRadius: DesignSystem.borderRadius.md,
              cursor: 'pointer',
              '&:hover': { backgroundColor: DesignSystem.colors.hover },
            }}
          >
            <Avatar
              src={avatarSrc || undefined}
              sx={{
                width: 34,
                height: 34,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: DesignSystem.colors.surfaceElevated,
                color: DesignSystem.colors.textPrimary,
              }}
            >
              {!avatarSrc ? initials : null}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: DesignSystem.colors.textPrimary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user ? displayName : t('navGuest')}
              </Typography>
              <Typography sx={{ fontSize: 11, color: DesignSystem.colors.textTertiary }}>{t('navProfile')}</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
